import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import i18n from 'i18n';
import { DIRECTION } from 'components/constants';
import {
  createCurrencyPairRequest,
  createMarkupGroupRequest,
  deleteMarkupGroupRequest,
  getCurrencyPairsListRequest,
  getMarkupGroupMarkupsListByIdRequest,
  getMarkupListRequest,
  renameMarkupGroupRequest,
  updateCurrencyPairRequest,
  updateMarkupGroupRequest
} from 'services/requestAgent';

class MarkupsStore {
  isInitialized = false;
  isLoading = false;
  isNewMarkupGroupSuccessfullyCreated = false;
  isNewCurrencyPairSuccessfullyCreated = false;
  isMarkupGroupSuccessfullyDeleted = false;
  isNewMarkupToMarkupGroupSuccessfullyCreated = false;
  isNewIndividualMarkupSuccessfullyCreated = false;
  isCustomerMarkupListUpdated = false;
  error = null;
  markupGroups = null;
  currentMarkupGroupDetails = null;
  customerMarkupList = null;
  currencyPairList = null;
  pagination = {
    sortBy: null,
    page: 0,
    size: 20,
    totalPages: 0,
    totalElements: 0,
    direction: DIRECTION.ASC
  };
  filters = {
    searchText: ''
  };
  markupsListFilters = {
    from: '',
    fromNetwork: '',
    to: '',
    toNetwork: '',
    activeOnly: false,
    manualOnly: false
  };

  constructor() {
    makeAutoObservable(this);
  }

  resetMarkupsStore = () => {
    this.isInitialized = false;
    this.isLoading = false;
    this.isNewMarkupGroupSuccessfullyCreated = false;
    this.isNewCurrencyPairSuccessfullyCreated = false;
    this.isMarkupGroupSuccessfullyDeleted = false;
    this.isNewMarkupToMarkupGroupSuccessfullyCreated = false;
    this.isNewIndividualMarkupSuccessfullyCreated = false;
    this.isCustomerMarkupListUpdated = false;
    this.error = null;
    this.markupGroups = null;
    this.currentMarkupGroupDetails = null;
    this.customerMarkupList = null;
    this.currencyPairList = null;
    this.pagination = {
      sortBy: null,
      page: 0,
      size: 20,
      totalPages: 0,
      totalElements: 0,
      direction: DIRECTION.ASC
    };
    this.filters = {
      searchText: ''
    };
    this.markupsListFilters = {
      from: '',
      fromNetwork: '',
      to: '',
      toNetwork: '',
      activeOnly: false,
      manualOnly: false
    };
  };

  setIsLoading = (status) => {
    this.isLoading = status;
    this.error = null;
  };

  setIsNewMarkupGroupSuccessfullyCreated = (status) => {
    this.isNewMarkupGroupSuccessfullyCreated = status;
  };

  setIsNewCurrencyPairSuccessfullyCreated = (status) => {
    this.isNewCurrencyPairSuccessfullyCreated = status;
  };

  setIsMarkupGroupSuccessfullyDeleted = (status) => {
    this.isMarkupGroupSuccessfullyDeleted = status;
  };

  setIsNewMarkupToMarkupGroupSuccessfullyCreated = (status) => {
    this.isNewMarkupToMarkupGroupSuccessfullyCreated = status;
  };

  setIsNewIndividualMarkupSuccessfullyCreated = (status) => {
    this.isNewIndividualMarkupSuccessfullyCreated = status;
  };

  setIsCustomerMarkupListUpdated = (status) => {
    this.isCustomerMarkupListUpdated = status;
  };

  setMarkupGroupsPage = (page) => {
    this.pagination.page = page;
  };

  setMarkupGroupsPageSize = (size) => {
    this.pagination.size = size;
  };

  setFilter = (fieldName, value) => {
    this.filters[fieldName] = value;
    this.pagination.page = 0;
  };

  setMarkupsListFilter = (fieldName, value) => {
    this.markupsListFilters[fieldName] = value;
  };

  setSortData = (sortData) => {
    this.pagination.sortBy = sortData.sortBy;
    this.pagination.direction = sortData.direction;
  };

  setFiltersFromUrl = (params) => {
    // Copying parameters from the URL to the filters object properties
    this.filters.searchText = params.searchText || this.filters.searchText;

    // Converting pagination parameters from the URL
    this.pagination.page = parseInt(params.page) || this.pagination.page;
    this.pagination.size = parseInt(params.size) || this.pagination.size;

    // Converting sorting parameters from the URL
    this.pagination.sortBy = params.sort_column || this.pagination.sortBy;
    this.pagination.direction = params.sort_direction || this.pagination.direction;
  };

  setMarkupsListFiltersFromUrl = (params) => {
    const filters = { ...this.markupsListFilters };

    // Mapping URL filter parameters to corresponding properties in the 'markupsListFilters' object
    const filterParamsMapping = {
      from: 'from',
      fromNetwork: 'fromNetwork',
      to: 'to',
      toNetwork: 'toNetwork'
    };

    // Iterating over each URL parameter and assigning its values to 'markupsListFilters'
    for (const param in params) {
      if (param in filterParamsMapping) {
        let value = params[param];

        // Handle only array values as arrays, keep others as their original types
        if (Array.isArray(value)) {
          filters[filterParamsMapping[param]] = value;
        } else {
          filters[filterParamsMapping[param]] = value;
        }
      }
    }

    this.markupsListFilters = filters;
  };

  prepareMarkupsListFiltersParams = () => {
    const params = {};

    // Map frontend field names to API field names
    const fieldMapping = {
      from: 'currency_from',
      fromNetwork: 'currency_from_network',
      to: 'currency_to',
      toNetwork: 'currency_to_network',
      activeOnly: 'active_only',
      manualOnly: 'manual_only'
    };

    Object.entries(this.markupsListFilters).forEach(([key, value]) => {
      if (value) {
        const apiKey = fieldMapping[key] || key;
        params[apiKey] = value;
      }
    });

    return params;
  };

  getMarkupGroupsList = async (pageSize = undefined) => {
    this.setIsLoading(true);
    try {
      const {
        content: markupGroupsList,
        number: page,
        size,
        total_elements: totalElements,
        total_pages: totalPages
      } = await getMarkupListRequest(
        this.pagination.sortBy,
        this.pagination.page,
        pageSize ? pageSize : this.pagination.size,
        this.pagination.direction,
        this.filters.searchText
      );

      runInAction(() => {
        this.isInitialized = true;
        this.markupGroups = markupGroupsList;
        this.pagination = {
          ...this.pagination,
          page,
          size: !pageSize ? size : this.pagination.size,
          totalElements,
          totalPages
        };
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  getMarkupGroupMarkupsListById = async (markupGroupId) => {
    this.setIsLoading(true);
    try {
      const queryParams = this.prepareMarkupsListFiltersParams();
      const markupGroupMarkupsList = await getMarkupGroupMarkupsListByIdRequest(markupGroupId, queryParams);

      const currentMarkupGroupDetails = this.markupGroups.find(
        (markupGroup) => markupGroup.markupGroupId === markupGroupId
      );

      currentMarkupGroupDetails.markups = markupGroupMarkupsList;

      runInAction(() => {
        this.isInitialized = true;
        this.currentMarkupGroupDetails = currentMarkupGroupDetails;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  createMarkupGroup = async (data) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(createMarkupGroupRequest(data), {
      pending: i18n.getMessage('markupsStore.createMarkupGroup.toastPromise.pending'),
      success: i18n.getMessage('markupsStore.createMarkupGroup.toastPromise.success')
    });

    try {
      const newMarkupGroup = await toastPromise;

      runInAction(() => {
        this.currentMarkupGroupDetails = newMarkupGroup;
        this.isNewMarkupGroupSuccessfullyCreated = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  renameMarkupGroup = async (markupGroupId, data) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(renameMarkupGroupRequest(markupGroupId, data), {
      pending: i18n.getMessage('markupsStore.renameMarkupGroup.toastPromise.pending'),
      success: i18n.getMessage('markupsStore.renameMarkupGroup.toastPromise.success')
    });

    try {
      const updatedMarkupGroupDetails = await toastPromise;

      const updatedMarkupGroupList = this.markupGroups.map((markupGroup) =>
        markupGroup.markupGroupId === markupGroupId ? updatedMarkupGroupDetails : markupGroup
      );

      runInAction(() => {
        this.markupGroups = updatedMarkupGroupList;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteMarkupGroup = async (markupGroupId) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(deleteMarkupGroupRequest(markupGroupId), {
      pending: i18n.getMessage('markupsStore.deleteMarkupGroup.toastPromise.pending'),
      success: i18n.getMessage('markupsStore.deleteMarkupGroup.toastPromise.success')
    });

    try {
      await toastPromise;

      const updatedMarkupGroupList = this.markupGroups.filter(
        (markupGroup) => markupGroup.markupGroupId !== markupGroupId
      );

      runInAction(() => {
        this.markupGroups = updatedMarkupGroupList;
        this.isMarkupGroupSuccessfullyDeleted = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateMarkupGroup = async (markupGroupId, markups) => {
    this.setIsLoading(true);

    const markupRequestBody = markups?.map((markupGroup) => {
      return { markupId: markupGroup.id, markup: markupGroup.markup };
    });

    const toastPromise = toast.promise(updateMarkupGroupRequest(markupGroupId, markupRequestBody), {
      pending: i18n.getMessage('markupsStore.updateMarkupGroup.toastPromise.pending'),
      success: i18n.getMessage('markupsStore.updateMarkupGroup.toastPromise.success')
    });

    try {
      const updatedMarkupGroupMarkupsList = await toastPromise;

      runInAction(() => {
        this.currentMarkupGroupDetails.markups = updatedMarkupGroupMarkupsList;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  getCurrencyPairsList = async () => {
    this.setIsLoading(true);
    try {
      const queryParams = this.prepareMarkupsListFiltersParams();
      const {
        content: currencyPairList,
        number: page,
        size,
        total_elements: totalElements,
        total_pages: totalPages
      } = await getCurrencyPairsListRequest(this.pagination, queryParams);

      runInAction(() => {
        this.isInitialized = true;
        this.currencyPairList = currencyPairList;
        this.pagination = {
          ...this.pagination,
          page,
          size,
          totalElements,
          totalPages
        };
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  createCurrencyPair = async (data) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(createCurrencyPairRequest(data), {
      pending: i18n.getMessage('markupsStore.createCurrencyPair.toastPromise.pending'),
      success: i18n.getMessage('markupsStore.createCurrencyPair.toastPromise.success')
    });

    try {
      await toastPromise;

      runInAction(() => {
        this.isNewCurrencyPairSuccessfullyCreated = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateCurrencyPair = async (data) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(updateCurrencyPairRequest(data), {
      pending: i18n.getMessage('markupsStore.updateCurrencyPair.toastPromise.pending'),
      success: i18n.getMessage('markupsStore.updateCurrencyPair.toastPromise.success')
    });

    try {
      const updatedCurrencyPair = await toastPromise;

      const updatedCurrencyPairList = this.currencyPairList.map((rate) =>
        rate.id === updatedCurrencyPair.id ? updatedCurrencyPair : rate
      );

      runInAction(() => {
        this.currencyPairList = updatedCurrencyPairList;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  getCurrencyPairsListForCreatingMarkupGroup = async () => {
    this.setIsLoading(true);

    const pagination = {
      ...this.pagination,
      size: 1000
    };

    try {
      const queryParams = this.prepareMarkupsListFiltersParams();
      const {
        content: currencyPairsList,
        number: page,
        total_elements: totalElements,
        total_pages: totalPages
      } = await getCurrencyPairsListRequest(pagination, queryParams);

      //eslint-disable-next-line no-unused-vars
      const updatedCurrencyPairsList = currencyPairsList.map(({ active, created_at, ...currencyPair }) => ({
        ...currencyPair,
        currencyPairId: currencyPair.id,
        markup: 0
      }));

      runInAction(() => {
        this.isInitialized = true;
        this.currencyPairList = updatedCurrencyPairsList;
        this.pagination = {
          ...this.pagination,
          page,
          size: 1000,
          totalElements,
          totalPages
        };
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };
}

export default new MarkupsStore();
